import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import surveyService from '../services/surveyService';
import { createProject } from '../services/sketchService';
import toast from 'react-hot-toast';
import SketchEditor from './SketchEditor';
import FileUpload from './FileUpload';
import ConfirmModal from './ConfirmModal';
import {
  Plus, Edit2, Trash2, X, ChevronDown, ChevronUp,
  Home, Bed, Sofa, Bath, DoorOpen, Car, Briefcase, MoreHorizontal,
  Clock, CheckCircle, AlertCircle,
  Box, Lightbulb, Wrench, FileText, PenTool, Image
} from 'lucide-react';

// Tipos de ambiente disponíveis
const ENVIRONMENT_TYPES = [
  { value: 'kitchen', label: 'Cozinha', icon: Home },
  { value: 'bedroom', label: 'Quarto', icon: Bed },
  { value: 'living_room', label: 'Sala', icon: Sofa },
  { value: 'bathroom', label: 'Banheiro', icon: Bath },
  { value: 'office', label: 'Escritório', icon: Briefcase },
  { value: 'garage', label: 'Garagem', icon: Car },
  { value: 'laundry', label: 'Área de Serviço', icon: DoorOpen },
  { value: 'balcony', label: 'Varanda', icon: DoorOpen },
  { value: 'other', label: 'Outro', icon: MoreHorizontal }
];

// Status disponíveis
const STATUS_OPTIONS = [
  { value: 'PENDING', label: 'Pendente', color: 'bg-gray-100 text-gray-700', icon: Clock },
  { value: 'IN_PROGRESS', label: 'Em Andamento', color: 'bg-blue-100 text-blue-700', icon: Clock },
  { value: 'COMPLETED', label: 'Concluído', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  { value: 'BLOCKED', label: 'Bloqueado', color: 'bg-red-100 text-red-700', icon: AlertCircle }
];

// Prioridades
const PRIORITY_OPTIONS = [
  { value: 'LOW', label: 'Baixa', color: 'text-gray-500' },
  { value: 'NORMAL', label: 'Normal', color: 'text-blue-500' },
  { value: 'HIGH', label: 'Alta', color: 'text-orange-500' },
  { value: 'URGENT', label: 'Urgente', color: 'text-red-500' }
];

// Categorias de itens
const ITEM_CATEGORIES = [
  { value: 'moveis', label: 'Móveis', icon: Sofa },
  { value: 'eletrodomesticos', label: 'Eletrodomésticos', icon: Box },
  { value: 'iluminacao', label: 'Iluminação', icon: Lightbulb },
  { value: 'acabamento', label: 'Acabamento', icon: Wrench },
  { value: 'outro', label: 'Outro', icon: MoreHorizontal }
];

// Unidades
const UNIT_OPTIONS = [
  { value: 'un', label: 'Unidade' },
  { value: 'm2', label: 'm²' },
  { value: 'm', label: 'Metro' },
  { value: 'conjunto', label: 'Conjunto' }
];

const SurveyEnvironments = ({ surveyId, surveyStatus, project3DStatus, onStart3DProject, readOnly = false }) => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingEnvironment, setEditingEnvironment] = useState(null);
  const [expandedEnvironment, setExpandedEnvironment] = useState(null);
  const [showItemForm, setShowItemForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedEnvironmentId, setSelectedEnvironmentId] = useState(null);
  const [showSketchEditor, setShowSketchEditor] = useState(false);
  const [sketchProjectId, setSketchProjectId] = useState(null);
  const [starting3D, setStarting3D] = useState(false);
  const [showDeleteEnvironmentConfirm, setShowDeleteEnvironmentConfirm] = useState(false);
  const [environmentToDelete, setEnvironmentToDelete] = useState(null);
  const [showDeleteItemConfirm, setShowDeleteItemConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showSchedule3DModal, setShowSchedule3DModal] = useState(false);

  // Buscar ambientes
  const { data: environmentsData, isLoading } = useQuery({
    queryKey: ['survey-environments', surveyId],
    queryFn: () => surveyService.getEnvironments(surveyId),
    enabled: !!surveyId
  });

  // Buscar arquivos do levantamento (para mostrar por ambiente)
  const { data: surveyFilesData } = useQuery({
    queryKey: ['survey-files', surveyId],
    queryFn: () => surveyService.getSurveyFiles(surveyId),
    enabled: !!surveyId
  });

  const surveyFiles = surveyFilesData?.files || [];

  // Filtrar arquivos por ambiente
  const getFilesByEnvironment = (environmentId) => {
    return surveyFiles.filter(f => f.environment_id === environmentId);
  };

  const environments = environmentsData?.environments || [];

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data) => surveyService.createEnvironment(surveyId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['survey-environments', surveyId]);
      toast.success('Ambiente criado com sucesso!');
      setShowForm(false);
    },
    onError: () => toast.error('Erro ao criar ambiente')
  });

  const updateMutation = useMutation({
    mutationFn: ({ environmentId, data }) => surveyService.updateEnvironment(surveyId, environmentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['survey-environments', surveyId]);
      toast.success('Ambiente atualizado com sucesso!');
      setEditingEnvironment(null);
    },
    onError: () => toast.error('Erro ao atualizar ambiente')
  });

  const deleteMutation = useMutation({
    mutationFn: (environmentId) => surveyService.deleteEnvironment(surveyId, environmentId),
    onSuccess: () => {
      queryClient.invalidateQueries(['survey-environments', surveyId]);
      toast.success('Ambiente excluído com sucesso!');
    },
    onError: () => toast.error('Erro ao excluir ambiente')
  });

  const createItemMutation = useMutation({
    mutationFn: ({ environmentId, data }) => surveyService.createEnvironmentItem(surveyId, environmentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['survey-environments', surveyId]);
      toast.success('Item adicionado com sucesso!');
      setShowItemForm(false);
      setEditingItem(null);
    },
    onError: () => toast.error('Erro ao adicionar item')
  });

  const updateItemMutation = useMutation({
    mutationFn: ({ environmentId, itemId, data }) => surveyService.updateEnvironmentItem(surveyId, environmentId, itemId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['survey-environments', surveyId]);
      toast.success('Item atualizado com sucesso!');
      setEditingItem(null);
    },
    onError: () => toast.error('Erro ao atualizar item')
  });

  const deleteItemMutation = useMutation({
    mutationFn: ({ environmentId, itemId }) => surveyService.deleteEnvironmentItem(surveyId, environmentId, itemId),
    onSuccess: () => {
      queryClient.invalidateQueries(['survey-environments', surveyId]);
      toast.success('Item excluído com sucesso!');
    },
    onError: () => toast.error('Erro ao excluir item')
  });

  // Função para criar croqui para o ambiente
  const handleCreateSketchForEnvironment = async (environment) => {
    try {
      // Criar projeto de croqui
      const response = await createProject({
        name: `Croqui - ${environment.environment_name}`,
        description: `Croqui do ambiente ${environment.environment_name} - Levantamento #${surveyId}`,
        survey_id: surveyId,
        environment_id: environment.id
      });

      const newSketchId = response.data.id;

      // Atualizar ambiente com o sketch_project_id
      await surveyService.updateEnvironment(surveyId, environment.id, {
        sketch_project_id: newSketchId
      });

      // Abrir o editor
      setSketchProjectId(newSketchId);
      setSelectedEnvironmentId(environment.id);
      setShowSketchEditor(true);

      // Recarregar ambientes
      queryClient.invalidateQueries(['survey-environments', surveyId]);

      toast.success('Croqui criado com sucesso!');
    } catch (error) {
      console.error('Erro ao criar croqui:', error);
      toast.error('Erro ao criar croqui: ' + (error.response?.data?.message || error.message));
    }
  };

  // Função para fechar o editor de croqui
  const handleCloseSketchEditor = () => {
    setShowSketchEditor(false);
    setSketchProjectId(null);
    setSelectedEnvironmentId(null);
    // Recarregar ambientes para pegar o sketch_project_id atualizado
    queryClient.invalidateQueries(['survey-environments', surveyId]);
  };

  // Buscar itens de um ambiente específico
  const { data: itemsData, refetch: refetchItems } = useQuery({
    queryKey: ['environment-items', surveyId, selectedEnvironmentId],
    queryFn: () => surveyService.getEnvironmentItems(surveyId, selectedEnvironmentId),
    enabled: !!selectedEnvironmentId
  });

  const items = itemsData?.items || [];

  useEffect(() => {
    if (selectedEnvironmentId) {
      refetchItems();
    }
  }, [selectedEnvironmentId, refetchItems]);

  const handleSaveEnvironment = (formData) => {
    if (editingEnvironment) {
      updateMutation.mutate({ environmentId: editingEnvironment.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleSaveItem = (formData) => {
    if (editingItem) {
      updateItemMutation.mutate({
        environmentId: selectedEnvironmentId,
        itemId: editingItem.id,
        data: formData
      });
    } else {
      createItemMutation.mutate({ environmentId: selectedEnvironmentId, data: formData });
    }
  };

  const getStatusInfo = (status) => {
    return STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
  };

  const getPriorityInfo = (priority) => {
    return PRIORITY_OPTIONS.find(p => p.value === priority) || PRIORITY_OPTIONS[1];
  };

  const getEnvironmentTypeInfo = (type) => {
    return ENVIRONMENT_TYPES.find(t => t.value === type) || ENVIRONMENT_TYPES[ENVIRONMENT_TYPES.length - 1];
  };

  // Calcular totais
  const calculateTotals = () => {
    const completed = environments.filter(e => e.status === 'COMPLETED').length;
    const progress = environments.length > 0 ? Math.round((completed / environments.length) * 100) : 0;

    // Contar ambientes com croqui
    const withSketch = environments.filter(e => e.sketch_project_id).length;
    const sketchProgress = environments.length > 0 ? Math.round((withSketch / environments.length) * 100) : 0;

    // Verificar se pode iniciar 3D (todos ambientes com croqui, status adequado, 3D não iniciado)
    const canStart3D = environments.length > 0
      && withSketch === environments.length
      && !['EM_PROJETACAO', 'EM_REVISAO', 'APRESENTADO_CLIENTE', 'ORCAMENTO_PRONTO', 'ORCAMENTO_APROVADO'].includes(surveyStatus)
      && (!project3DStatus || project3DStatus === 'PENDING');

    return { progress, total: environments.length, completed, withSketch, sketchProgress, canStart3D };
  };

  const totals = calculateTotals();

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Ambientes do Levantamento</h3>
          <p className="text-sm text-gray-500">
            {environments.length} ambiente(s) • Progresso: {totals.progress}%
          </p>
        </div>
        {!readOnly && (
          <button
            onClick={() => {
              setEditingEnvironment(null);
              setShowForm(true);
            }}
            className="flex items-center gap-2 px-3 sm:px-4 py-3 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Novo Ambiente</span>
          </button>
        )}
      </div>

      {/* Resumo */}
      <div className="p-4 bg-gray-50 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <div className="bg-white p-3 rounded-lg shadow-sm">
          <div className="text-xs text-gray-500">Progresso</div>
          <div className="text-xl font-bold text-blue-600">{totals.progress}%</div>
          <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
            <div
              className="bg-blue-600 h-2 rounded-full"
              style={{ width: `${totals.progress}%` }}
            />
          </div>
        </div>

        <div className="bg-white p-3 rounded-lg shadow-sm">
          <div className="text-xs text-gray-500">Croquis 2D</div>
          <div className="text-xl font-bold text-purple-600">{totals.withSketch}/{totals.total}</div>
          <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
            <div
              className="bg-purple-600 h-2 rounded-full"
              style={{ width: `${totals.sketchProgress}%` }}
            />
          </div>
          <div className="text-xs text-gray-400 mt-1">
            {totals.withSketch === totals.total && totals.total > 0 ? '✓ Completo' : 'Faltam croquis'}
          </div>
        </div>

        {/* Botão Iniciar 3D */}
        {totals.canStart3D && !readOnly && (
          <div className="bg-purple-50 p-3 rounded-lg shadow-sm border border-purple-200 col-span-2">
            <div className="text-xs text-purple-600 mb-2">Projeto 3D</div>
            <button
              onClick={async () => {
                setStarting3D(true);
                try {
                  await onStart3DProject();
                } finally {
                  setStarting3D(false);
                }
              }}
              disabled={starting3D}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              <Box className="w-4 h-4" />
              {starting3D ? 'Iniciando...' : 'Iniciar Projeto 3D'}
            </button>
            <div className="text-xs text-purple-500 mt-1">
              Ambientes com croqui: {totals.withSketch}/{totals.total}
            </div>
          </div>
        )}

        {/* Status 3D em andamento */}
        {['EM_PROJETACAO', 'EM_REVISAO', 'APRESENTADO_CLIENTE', 'ORCAMENTO_PRONTO', 'ORCAMENTO_APROVADO'].includes(surveyStatus) && project3DStatus && project3DStatus !== 'PENDING' && (
          <div className="bg-green-50 p-3 rounded-lg shadow-sm border border-green-200 col-span-2">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-green-600 mb-1">Projeto 3D</div>
                <div className="text-sm font-medium text-green-700">
                  {project3DStatus === 'IN_PROGRESS' ? '🔄 Em Andamento' :
                   project3DStatus === 'COMPLETED' ? '✅ Concluído' :
                   project3DStatus === 'REVIEW' ? '👁️ Em Revisão' :
                   project3DStatus === 'APPROVED' ? '🎉 Aprovado' : project3DStatus}
                </div>
              </div>
              {project3DStatus === 'IN_PROGRESS' && !readOnly && (
                <button
                  onClick={() => setShowSchedule3DModal(true)}
                  className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700"
                >
                  <Clock className="w-3 h-3" />
                  Agendar Trabalho
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Formulário de Ambiente */}
      {showForm && (
        <EnvironmentForm
          environment={editingEnvironment}
          onSave={handleSaveEnvironment}
          onCancel={() => {
            setShowForm(false);
            setEditingEnvironment(null);
          }}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />
      )}

      {/* Lista de Ambientes */}
      <div className="p-4">
        {isLoading ? (
          <div className="text-center py-8 text-gray-500">Carregando ambientes...</div>
        ) : environments.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Home className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>Nenhum ambiente adicionado ainda.</p>
            {!readOnly && (
              <button
                onClick={() => setShowForm(true)}
                className="mt-2 text-blue-600 hover:underline"
              >
                Adicionar primeiro ambiente
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {environments.map((env) => {
              const isExpanded = expandedEnvironment === env.id;
              const TypeIcon = getEnvironmentTypeInfo(env.environment_type).icon;
              const statusInfo = getStatusInfo(env.status);
              const StatusIcon = statusInfo.icon;
              const priorityInfo = getPriorityInfo(env.priority);

              return (
                <div key={env.id} className="border border-gray-200 rounded-lg overflow-hidden">
                  {/* Ambiente Header */}
                  <div
                    className={`p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 ${isExpanded ? 'bg-blue-50' : ''}`}
                    onClick={() => setExpandedEnvironment(isExpanded ? null : env.id)}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${isExpanded ? 'bg-blue-100' : 'bg-gray-100'}`}>
                        <TypeIcon className={`w-5 h-5 ${isExpanded ? 'text-blue-600' : 'text-gray-600'}`} />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{env.environment_name}</div>
                        <div className="text-sm text-gray-500">
                          {getEnvironmentTypeInfo(env.environment_type).label}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
                        <StatusIcon className="w-3 h-3" />
                        {statusInfo.label}
                      </span>
                      <span className={`text-xs font-medium ${priorityInfo.color}`}>
                        {priorityInfo.label}
                      </span>
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>

                  {/* Ambiente Detalhes */}
                  {isExpanded && (
                    <div className="border-t border-gray-200 p-4 bg-gray-50">
                      {/* Dimensões */}
                      {env.dimensions && Object.keys(env.dimensions).length > 0 && (
                        <div className="mb-4 p-3 bg-white rounded-lg">
                          <div className="text-xs text-gray-500 mb-1">Dimensões</div>
                          <div className="flex gap-4 text-sm">
                            {env.dimensions.width && <span>Largura: {env.dimensions.width}m</span>}
                            {env.dimensions.height && <span>Altura: {env.dimensions.height}m</span>}
                            {env.dimensions.length && <span>Comprimento: {env.dimensions.length}m</span>}
                          </div>
                        </div>
                      )}

                      {/* Observações Técnicas */}
                      {env.technical_notes && (
                        <div className="mb-4 p-3 bg-white rounded-lg">
                          <div className="text-xs text-gray-500 mb-1">Observações Técnicas</div>
                          <p className="text-sm">{env.technical_notes}</p>
                        </div>
                      )}

                      {/* Observações Gerais */}
                      {env.observations && (
                        <div className="mb-4 p-3 bg-white rounded-lg">
                          <div className="text-xs text-gray-500 mb-1">Observações</div>
                          <p className="text-sm">{env.observations}</p>
                        </div>
                      )}

                      {/* Mídias do Ambiente */}
                      <div className="mb-4 p-4 bg-green-50 rounded-lg border border-green-200">
                        <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                          <Image className="w-4 h-4" />
                          Fotos do Ambiente
                        </h4>
                        <FileUpload
                          surveyId={surveyId}
                          environmentId={env.id}
                          existingFiles={getFilesByEnvironment(env.id)}
                          onFilesChange={(newFiles) => {
                            queryClient.invalidateQueries(['survey-files', surveyId]);
                          }}
                          disabled={readOnly}
                        />
                      </div>

                      {/* Preview do Croqui */}
                      {env.sketch_project_id ? (
                        <div className="mb-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                          <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                            <PenTool className="w-4 h-4" />
                            Croqui 2D
                          </h4>
                          {env.sketch_snapshot_image ? (
                            <div className="relative">
                              <img
                                src={`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/${env.sketch_snapshot_image}`}
                                alt="Croqui do ambiente"
                                className="w-full max-h-48 object-contain rounded border border-purple-300 bg-white"
                              />
                              <div className="mt-2 flex gap-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSketchProjectId(env.sketch_project_id);
                                    setSelectedEnvironmentId(env.id);
                                    setShowSketchEditor(true);
                                  }}
                                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                                >
                                  <PenTool className="w-4 h-4" />
                                  Editar Croqui
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="text-center py-4 text-purple-600">
                              <PenTool className="w-8 h-8 mx-auto mb-2 opacity-50" />
                              <p className="text-sm">Croqui criado, aguardando salvamento...</p>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSketchProjectId(env.sketch_project_id);
                                  setSelectedEnvironmentId(env.id);
                                  setShowSketchEditor(true);
                                }}
                                className="mt-2 flex items-center gap-1 px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 mx-auto"
                              >
                                <PenTool className="w-4 h-4" />
                                Abrir e Salvar Croqui
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="mb-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                          <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                            <PenTool className="w-4 h-4" />
                            Croqui 2D
                          </h4>
                          <div className="text-center py-4">
                            <PenTool className="w-8 h-8 mx-auto mb-2 text-purple-400" />
                            <p className="text-sm text-gray-600 mb-3">Nenhum croqui criado ainda</p>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCreateSketchForEnvironment(env);
                              }}
                              className="flex items-center gap-1 px-3 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 mx-auto"
                            >
                              <Plus className="w-4 h-4" />
                              Criar Croqui
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Ações */}
                      {!readOnly && (
                        <div className="flex gap-2 mb-4">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingEnvironment(env);
                              setShowForm(true);
                            }}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                          >
                            <Edit2 className="w-4 h-4" />
                            Editar
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEnvironmentToDelete(env);
                              setShowDeleteEnvironmentConfirm(true);
                            }}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-white border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                            Excluir
                          </button>
                        </div>
                      )}

                      {/* Itens do Ambiente */}
                      <div className="mt-4">
                        <div className="flex justify-between items-center mb-2">
                          <h4 className="font-medium text-gray-900 flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            Itens do Ambiente
                          </h4>
                          {!readOnly && (
                            <button
                              onClick={() => {
                                setSelectedEnvironmentId(env.id);
                                setEditingItem(null);
                                setShowItemForm(true);
                              }}
                              className="flex items-center gap-1 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded"
                            >
                              <Plus className="w-3 h-3" />
                              Adicionar Item
                            </button>
                          )}
                        </div>

                        {/* Formulário de Item */}
                        {showItemForm && selectedEnvironmentId === env.id && (
                          <div className="mb-4 p-3 bg-white rounded-lg border border-blue-200">
                            <ItemForm
                              item={editingItem}
                              onSave={handleSaveItem}
                              onCancel={() => {
                                setShowItemForm(false);
                                setEditingItem(null);
                              }}
                              isLoading={createItemMutation.isPending || updateItemMutation.isPending}
                            />
                          </div>
                        )}

                        {/* Lista de Itens */}
                        {items.filter(i => i.environment_id === env.id || !i.environment_id).length > 0 ? (
                          <div className="space-y-2">
                            {items
                              .filter(item => item.environment_id === env.id)
                              .map((item) => (
                                <div key={item.id} className="flex items-center justify-between p-2 bg-white rounded border border-gray-200">
                                  <div>
                                    <div className="font-medium text-sm">{item.item_name}</div>
                                    <div className="text-xs text-gray-500">
                                      {ITEM_CATEGORIES.find(c => c.value === item.category)?.label || item.category} •
                                      {item.quantity} {UNIT_OPTIONS.find(u => u.value === item.unit)?.label} •
                                      R$ {parseFloat(item.estimated_price || 0).toLocaleString('pt-BR')}
                                    </div>
                                  </div>
                                  {!readOnly && (
                                    <div className="flex gap-1">
                                      <button
                                        onClick={() => {
                                          setEditingItem(item);
                                          setSelectedEnvironmentId(env.id);
                                          setShowItemForm(true);
                                        }}
                                        className="p-1 text-gray-400 hover:text-blue-600"
                                      >
                                        <Edit2 className="w-3 h-3" />
                                      </button>
                                      <button
                                        onClick={() => {
                                          setItemToDelete({ environmentId: env.id, itemId: item.id });
                                          setShowDeleteItemConfirm(true);
                                        }}
                                        className="p-1 text-gray-400 hover:text-red-600"
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500 text-center py-2">
                            Nenhum item adicionado ainda.
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal do Editor de Croqui */}
      {showSketchEditor && sketchProjectId && (
        <SketchEditorModal
          show={showSketchEditor}
          sketchProjectId={sketchProjectId}
          environmentName={environments.find(e => e.id === selectedEnvironmentId)?.environment_name || 'Ambiente'}
          surveyId={surveyId}
          onClose={handleCloseSketchEditor}
        />
      )}

      {/* Modal de Agendamento de Trabalho 3D */}
      <Schedule3DWorkModal
        show={showSchedule3DModal}
        surveyId={surveyId}
        onClose={() => setShowSchedule3DModal(false)}
        onSuccess={() => {
          queryClient.invalidateQueries(['survey-environments', surveyId]);
        }}
      />

      {/* Confirm Modal for Environment Delete */}
      <ConfirmModal
        show={showDeleteEnvironmentConfirm}
        onConfirm={() => {
          if (environmentToDelete) {
            deleteMutation.mutate(environmentToDelete.id);
          }
          setShowDeleteEnvironmentConfirm(false);
          setEnvironmentToDelete(null);
        }}
        onCancel={() => {
          setShowDeleteEnvironmentConfirm(false);
          setEnvironmentToDelete(null);
        }}
        title="Excluir Ambiente"
        message={`Tem certeza que deseja excluir o ambiente "${environmentToDelete?.environment_name}"? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
      />

      {/* Confirm Modal for Item Delete */}
      <ConfirmModal
        show={showDeleteItemConfirm}
        onConfirm={() => {
          if (itemToDelete) {
            deleteItemMutation.mutate(itemToDelete);
          }
          setShowDeleteItemConfirm(false);
          setItemToDelete(null);
        }}
        onCancel={() => {
          setShowDeleteItemConfirm(false);
          setItemToDelete(null);
        }}
        title="Excluir Item"
        message="Tem certeza que deseja excluir este item? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
      />
    </div>
  );
};

// Formulário de Ambiente
const EnvironmentForm = ({ environment, onSave, onCancel, isLoading }) => {
  const [formData, setFormData] = useState({
    environment_name: environment?.environment_name || '',
    environment_type: environment?.environment_type || 'kitchen',
    wall_type: environment?.wall_type || '',
    floor_type: environment?.floor_type || '',
    ceiling_type: environment?.ceiling_type || '',
    technical_notes: environment?.technical_notes || '',
    observations: environment?.observations || '',
    dimensions: environment?.dimensions || { width: '', height: '', length: '' },
    has_plumbing: environment?.has_plumbing || false,
    has_electrical_points: environment?.has_electrical_points || '',
    priority: environment?.priority || 'NORMAL',
    status: environment?.status || 'PENDING'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleDimensionChange = (field, value) => {
    setFormData({
      ...formData,
      dimensions: { ...formData.dimensions, [field]: value }
    });
  };

  return (
    <form onSubmit={handleSubmit} className="p-4 bg-blue-50 border-b border-blue-200">
      <div className="flex justify-between items-center mb-4">
        <h4 className="font-medium text-gray-900">
          {environment ? 'Editar Ambiente' : 'Novo Ambiente'}
        </h4>
        <button type="button" onClick={onCancel} className="text-gray-400 hover:text-gray-600">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Ambiente *</label>
          <input
            type="text"
            value={formData.environment_name}
            onChange={(e) => setFormData({ ...formData, environment_name: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="Ex: Cozinha Principal"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
          <select
            value={formData.environment_type}
            onChange={(e) => setFormData({ ...formData, environment_type: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            {ENVIRONMENT_TYPES.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="PENDING">Pendente</option>
            <option value="IN_PROGRESS">Em Andamento</option>
            <option value="COMPLETED">Concluído</option>
            <option value="BLOCKED">Bloqueado</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Prioridade</label>
          <select
            value={formData.priority}
            onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="LOW">Baixa</option>
            <option value="NORMAL">Normal</option>
            <option value="HIGH">Alta</option>
            <option value="URGENT">Urgente</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Parede</label>
          <input
            type="text"
            value={formData.wall_type}
            onChange={(e) => setFormData({ ...formData, wall_type: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="Ex: Alvenaria, Drywall..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Piso</label>
          <input
            type="text"
            value={formData.floor_type}
            onChange={(e) => setFormData({ ...formData, floor_type: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="Ex: Cerâmica, Porcelanato..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Teto</label>
          <input
            type="text"
            value={formData.ceiling_type}
            onChange={(e) => setFormData({ ...formData, ceiling_type: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="Ex: Gesso, Laje..."
          />
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Largura (m)</label>
          <input
            type="number"
            step="0.01"
            value={formData.dimensions.width}
            onChange={(e) => handleDimensionChange('width', e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="0.00"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Altura (m)</label>
          <input
            type="number"
            step="0.01"
            value={formData.dimensions.height}
            onChange={(e) => handleDimensionChange('height', e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="0.00"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Comprimento (m)</label>
          <input
            type="number"
            step="0.01"
            value={formData.dimensions.length}
            onChange={(e) => handleDimensionChange('length', e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="0.00"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Pontos Elétricos</label>
          <input
            type="number"
            value={formData.has_electrical_points}
            onChange={(e) => setFormData({ ...formData, has_electrical_points: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="0"
            min="0"
          />
        </div>
      </div>

      <div className="mt-4 flex items-center gap-6">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.has_plumbing}
            onChange={(e) => setFormData({ ...formData, has_plumbing: e.target.checked })}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">Possui hidráulica</span>
        </label>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Observações Técnicas</label>
          <textarea
            value={formData.technical_notes}
            onChange={(e) => setFormData({ ...formData, technical_notes: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            rows={2}
            placeholder="Limitações técnicas, considerações especiais..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Observações Gerais</label>
          <textarea
            value={formData.observations}
            onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            rows={2}
            placeholder="Observações adicionais..."
          />
        </div>
      </div>

      <div className="mt-4 flex gap-2 justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 sm:px-6 py-3 sm:py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={isLoading || !formData.environment_name}
          className="px-4 sm:px-6 py-3 sm:py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {isLoading ? 'Salvando...' : 'Salvar'}
        </button>
      </div>
    </form>
  );
};

// Formulário de Item
const ItemForm = ({ item, onSave, onCancel, isLoading }) => {
  const [formData, setFormData] = useState({
    item_name: item?.item_name || '',
    category: item?.category || 'moveis',
    description: item?.description || '',
    quantity: item?.quantity || 1,
    unit: item?.unit || 'un',
    estimated_price: item?.estimated_price || '',
    observations: item?.observations || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      quantity: parseInt(formData.quantity) || 1,
      estimated_price: formData.estimated_price ? parseFloat(formData.estimated_price) : 0
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="flex justify-between items-center">
        <h5 className="font-medium text-gray-900">
          {item ? 'Editar Item' : 'Novo Item'}
        </h5>
        <button type="button" onClick={onCancel} className="text-gray-400 hover:text-gray-600">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="block text-xs font-medium text-gray-700 mb-1">Nome do Item *</label>
          <input
            type="text"
            value={formData.item_name}
            onChange={(e) => setFormData({ ...formData, item_name: e.target.value })}
            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
            placeholder="Ex: Armário Superior"
            required
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Categoria</label>
          <select
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
          >
            {ITEM_CATEGORIES.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
        </div>

        <div className="flex gap-2">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-700 mb-1">Qtd</label>
            <input
              type="number"
              min="1"
              value={formData.quantity}
              onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-700 mb-1">Unidade</label>
            <select
              value={formData.unit}
              onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
            >
              {UNIT_OPTIONS.map(unit => (
                <option key={unit.value} value={unit.value}>{unit.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="col-span-2">
          <label className="block text-xs font-medium text-gray-700 mb-1">Preço Estimado (R$)</label>
          <input
            type="number"
            step="0.01"
            value={formData.estimated_price}
            onChange={(e) => setFormData({ ...formData, estimated_price: e.target.value })}
            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
            placeholder="0.00"
          />
        </div>

        <div className="col-span-2">
          <label className="block text-xs font-medium text-gray-700 mb-1">Descrição</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
            rows={2}
            placeholder="Detalhes do item..."
          />
        </div>
      </div>

      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={isLoading || !formData.item_name}
          className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {isLoading ? 'Salvando...' : 'Salvar'}
        </button>
      </div>
    </form>
  );
};

// Modal do Editor de Croqui
const SketchEditorModal = ({ show, sketchProjectId, environmentName, surveyId, onClose }) => {
  if (!show || !sketchProjectId) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[95vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Croqui - {environmentName}
            </h2>
            <p className="text-sm text-gray-500">Levantamento #{surveyId}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="overflow-y-auto max-h-[calc(95vh-80px)]">
          <SketchEditor
            projectId={sketchProjectId}
            onClose={onClose}
          />
        </div>
      </div>
    </div>
  );
};

// Modal de Agendamento de Trabalho 3D
const Schedule3DWorkModal = ({ show, surveyId, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    projetista_id: '',
    data_inicio: new Date().toISOString().split('T')[0],
    horas_estimadas: '',
    horas_por_dia: 4
  });
  const [isLoading, setIsLoading] = useState(false);

  const { data: designersData } = useQuery({
    queryKey: ['users', 'projetistas'],
    queryFn: async () => {
      const response = await fetch('/api/users?role=RECEPCAO_PROJETOS');
      const data = await response.json();
      return data;
    },
    enabled: show
  });

  const designers = designersData?.users || [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch(`/api/surveys/${surveyId}/schedule-3d-work`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (response.ok) {
        toast.success(result.message || 'Trabalho 3D agendado com sucesso!');
        onSuccess?.();
        onClose();
      } else {
        toast.error(result.message || 'Erro ao agendar trabalho 3D');
      }
    } catch (error) {
      console.error('Erro ao agendar trabalho 3D:', error);
      toast.error('Erro ao agendar trabalho 3D');
    } finally {
      setIsLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Agendar Trabalho 3D</h2>
            <p className="text-sm text-gray-500">Levantamento #{surveyId}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Projetista Responsável
            </label>
            <select
              value={formData.projetista_id}
              onChange={(e) => setFormData({ ...formData, projetista_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              required
            >
              <option value="">Selecione um projetista</option>
              {designers.map((d) => (
                <option key={d.id} value={d.id}>{d.full_name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data de Início
            </label>
            <input
              type="date"
              value={formData.data_inicio}
              onChange={(e) => setFormData({ ...formData, data_inicio: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Horas Estimadas (total)
            </label>
            <input
              type="number"
              value={formData.horas_estimadas}
              onChange={(e) => setFormData({ ...formData, horas_estimadas: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              placeholder="Ex: 20"
              min="1"
              step="0.5"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Horas por Dia
            </label>
            <input
              type="number"
              value={formData.horas_por_dia}
              onChange={(e) => setFormData({ ...formData, horas_por_dia: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              min="1"
              max="8"
              required
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              {isLoading ? 'Agendando...' : 'Agendar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SurveyEnvironments;
